import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOMeta from "@/components/feature/SEOMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = getArticleBySlug(slug || "");

  if (!article) {
    return <NotFound />;
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category);

  const breadcrumbItems = [
    ...(category
      ? [{ label: category.name, path: `/category/${category.slug}` }]
      : []),
    { label: article.title },
  ];

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "探花系列精品聚合平台",
      logo: {
        "@type": "ImageObject",
        url: "http://lalahzz.cn/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `http://lalahzz.cn/article/${article.slug}`,
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://lalahzz.cn/",
      },
      ...(category
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: category.name,
              item: `http://lalahzz.cn/category/${category.slug}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: `http://lalahzz.cn/article/${article.slug}`,
            },
          ]
        : [
            {
              "@type": "ListItem",
              position: 2,
              name: article.title,
              item: `http://lalahzz.cn/article/${article.slug}`,
            },
          ]),
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 探花系列精品聚合平台`}
        description={article.excerpt}
        keywords={`${article.title},${article.categoryName},探花系列,探花精选,国内探花`}
        canonical={`http://lalahzz.cn/article/${article.slug}`}
        ogImage={article.image}
        ogType="article"
        article={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author,
          section: article.categoryName,
          tags: [article.categoryName, article.tag],
        }}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />
      <Navbar />

      <main>
        {/* Article Header */}
        <section className="relative pt-24 md:pt-28 pb-8 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:gap-12">
              <div className="lg:w-[60%]">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full">
                    {article.tag}
                  </span>
                  <span className="text-xs text-foreground-500">
                    {article.categoryName}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
              </div>
              <div className="lg:w-[40%] mt-4 lg:mt-0">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-4 mt-4 text-xs text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line w-3 h-3 flex items-center justify-center"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
                    阅读时长 {article.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                    {article.views} 阅读
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Image */}
        <section className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="aspect-video rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-10 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-10">
            <article className="prose prose-sm md:prose-base max-w-none">
              <div
                className="text-foreground-700 leading-relaxed [&_p]:mb-4 [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-1.5"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-600">标签：</span>
                  <span className="text-xs font-medium text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full">
                    {article.tag}
                  </span>
                  <span className="text-xs font-medium text-secondary-700 bg-secondary-100 px-2.5 py-1 rounded-full">
                    {article.categoryName}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-200/70 text-sm text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
                    aria-label="分享文章"
                  >
                    <i className="ri-share-line w-4 h-4 flex items-center justify-center"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1.5">
                        {ra.date}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}